import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import PageLayout from '../layout/PageLayout';
import { InvaderService, LeaderboardEntry, MonthlyStatsEntry } from "../../services/InvaderService";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import './LeaderboardPage.css';

type TabType = 'global' | 'monthly';

const LeaderboardPage = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [activeTab, setActiveTab] = useState<TabType>('global');

    // Leaderboard global
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [loadingGlobal, setLoadingGlobal] = useState(true);
    const [errorGlobal, setErrorGlobal] = useState<string | null>(null);

    // Stats mensuelles
    const [monthlyStats, setMonthlyStats] = useState<MonthlyStatsEntry[]>([]);
    const [loadingMonthly, setLoadingMonthly] = useState(false);
    const [errorMonthly, setErrorMonthly] = useState<string | null>(null);

    // Selecteur de mois
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

    // Top 3 du mois courant (toujours charge)
    const [top3Monthly, setTop3Monthly] = useState<MonthlyStatsEntry[]>([]);
    const [loadingTop3, setLoadingTop3] = useState(true);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                const data = await InvaderService.fetchLeaderboard(getAccessTokenSilently);
                setLeaderboard(data);
            } catch (err) {
                setErrorGlobal(err instanceof Error ? err.message : "Erreur inconnue");
            } finally {
                setLoadingGlobal(false);
            }
        };

        fetchLeaderboard();
    }, [getAccessTokenSilently]);

    // Charger le top 3 du mois courant au chargement
    useEffect(() => {
        const fetchTop3 = async () => {
            const now = new Date();
            try {
                const data = await InvaderService.fetchMonthlyStats(getAccessTokenSilently, now.getFullYear(), now.getMonth() + 1);
                setTop3Monthly(data.slice(0, 3));
            } catch (err) {
                console.error("Erreur chargement top 3:", err);
            } finally {
                setLoadingTop3(false);
            }
        };
        fetchTop3();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (activeTab === 'monthly') {
            fetchMonthlyStats();
        }
    }, [activeTab, selectedYear, selectedMonth]);

    const fetchMonthlyStats = async () => {
        setLoadingMonthly(true);
        setErrorMonthly(null);
        try {
            const data = await InvaderService.fetchMonthlyStats(getAccessTokenSilently, selectedYear, selectedMonth);
            setMonthlyStats(data);
        } catch (err) {
            setErrorMonthly(err instanceof Error ? err.message : "Erreur inconnue");
        } finally {
            setLoadingMonthly(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getMedalClass = (position: number) => {
        switch (position) {
            case 1: return 'leaderboard__medal--gold';
            case 2: return 'leaderboard__medal--silver';
            case 3: return 'leaderboard__medal--bronze';
            default: return '';
        }
    };

    const getMonthName = (month: number) => {
        const months = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
                       'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
        return months[month - 1];
    };

    const handlePreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    const handleNextMonth = () => {
        const now = new Date();
        const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
        if (isCurrentMonth) return;

        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    const isNextDisabled = () => {
        const now = new Date();
        return selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
    };

    return (
        <PageLayout>
            <div className="content-layout">
                <div className="leaderboard-terminal">
                    <div className="leaderboard-terminal__header">
                        <div className="leaderboard-terminal__dots">
                            <span className="leaderboard-terminal__dot leaderboard-terminal__dot--red"></span>
                            <span className="leaderboard-terminal__dot leaderboard-terminal__dot--yellow"></span>
                            <span className="leaderboard-terminal__dot leaderboard-terminal__dot--green"></span>
                        </div>
                        <span className="leaderboard-terminal__title">~/leaderboard</span>
                    </div>

                    <div className="leaderboard-terminal__body">
                        <div className="leaderboard-header">
                            <EmojiEventsIcon className="leaderboard-header__icon" />
                            <h1 className="leaderboard-header__title">Classement</h1>
                        </div>

                        {/* Top 3 du mois */}
                        {!loadingTop3 && top3Monthly.length > 0 && (
                            <div className="top3-section">
                                <h2 className="top3-section__title">
                                    <CalendarMonthIcon className="top3-section__icon" />
                                    Top 3 - {getMonthName(new Date().getMonth() + 1)} {new Date().getFullYear()}
                                </h2>
                                <div className="top3-podium">
                                    {top3Monthly.map((entry, index) => (
                                        <div key={entry.odri} className={`top3-podium__item top3-podium__item--${index + 1}`}>
                                            <div className="top3-podium__rank">{index + 1}</div>
                                            <div className="top3-podium__pseudo">{entry.pseudo || "-"}</div>
                                            <div className="top3-podium__stats">
                                                <span className="top3-podium__flashs">{entry.flashCount} flashs</span>
                                                <span className="top3-podium__points">+{entry.pointsGained.toLocaleString()} pts</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="leaderboard-tabs">
                            <button
                                className={`leaderboard-tabs__tab ${activeTab === 'global' ? 'leaderboard-tabs__tab--active' : ''}`}
                                onClick={() => setActiveTab('global')}
                            >
                                Classement global
                            </button>
                            <button
                                className={`leaderboard-tabs__tab ${activeTab === 'monthly' ? 'leaderboard-tabs__tab--active' : ''}`}
                                onClick={() => setActiveTab('monthly')}
                            >
                                <CalendarMonthIcon className="leaderboard-tabs__icon" />
                                Ce mois-ci
                            </button>
                        </div>

                        {/* Global Leaderboard */}
                        {activeTab === 'global' && (
                            <>
                                {loadingGlobal ? (
                                    <div className="leaderboard-loading">
                                        <div className="leaderboard-loading__spinner"></div>
                                        <span>Chargement du classement...</span>
                                    </div>
                                ) : errorGlobal ? (
                                    <div className="leaderboard-error">
                                        Erreur: {errorGlobal}
                                    </div>
                                ) : leaderboard.length === 0 ? (
                                    <div className="leaderboard-empty">
                                        <span className="leaderboard-empty__icon">👾</span>
                                        <h3>Aucun joueur synchronise</h3>
                                        <p>Les joueurs apparaitront ici apres leur premiere synchronisation Flash Invaders.</p>
                                    </div>
                                ) : (
                                    <div className="leaderboard-table-container">
                                        <table className="leaderboard-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Pseudo</th>
                                                    <th className="hide-mobile">Score</th>
                                                    <th>Rang</th>
                                                    <th className="hide-mobile">Invaders</th>
                                                    <th className="hide-mobile">Villes</th>
                                                    <th className="hide-mobile">Sync</th>
                                                    <th className="show-mobile-only">Inv (Villes)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leaderboard.map((entry, index) => (
                                                    <tr key={entry.odri} className="leaderboard-table__row">
                                                        <td className={`leaderboard-table__cell--rank ${getMedalClass(index + 1)}`}>
                                                            {index + 1}
                                                        </td>
                                                        <td className="leaderboard-table__cell--pseudo">
                                                            {entry.pseudo || "-"}
                                                        </td>
                                                        <td className="leaderboard-table__cell--score hide-mobile">
                                                            {entry.score?.toLocaleString() || "-"}
                                                        </td>
                                                        <td className="leaderboard-table__cell--world-rank">
                                                            {entry.rank ? `#${entry.rank.toLocaleString()}` : "-"}
                                                        </td>
                                                        <td className="leaderboard-table__cell--invaders hide-mobile">
                                                            {entry.siFound?.toLocaleString() || "-"}
                                                        </td>
                                                        <td className="leaderboard-table__cell--cities hide-mobile">
                                                            {entry.cityFound || "-"}
                                                        </td>
                                                        <td className="leaderboard-table__cell--sync hide-mobile">
                                                            {formatDate(entry.lastSyncDate)}
                                                        </td>
                                                        <td className="leaderboard-table__cell--combined show-mobile-only">
                                                            {entry.siFound?.toLocaleString() || "-"} ({entry.cityFound || "-"})
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Monthly Stats */}
                        {activeTab === 'monthly' && (
                            <>
                                {/* Month Selector */}
                                <div className="month-selector">
                                    <button
                                        className="month-selector__btn"
                                        onClick={handlePreviousMonth}
                                    >
                                        &lt;
                                    </button>
                                    <span className="month-selector__label">
                                        {getMonthName(selectedMonth)} {selectedYear}
                                    </span>
                                    <button
                                        className="month-selector__btn"
                                        onClick={handleNextMonth}
                                        disabled={isNextDisabled()}
                                    >
                                        &gt;
                                    </button>
                                </div>

                                {loadingMonthly ? (
                                    <div className="leaderboard-loading">
                                        <div className="leaderboard-loading__spinner"></div>
                                        <span>Chargement des statistiques...</span>
                                    </div>
                                ) : errorMonthly ? (
                                    <div className="leaderboard-error">
                                        Erreur: {errorMonthly}
                                    </div>
                                ) : monthlyStats.length === 0 ? (
                                    <div className="leaderboard-empty">
                                        <span className="leaderboard-empty__icon">📅</span>
                                        <h3>Aucune activite ce mois</h3>
                                        <p>Aucun flash enregistre pour {getMonthName(selectedMonth)} {selectedYear}.</p>
                                    </div>
                                ) : (
                                    <div className="leaderboard-table-container">
                                        <table className="leaderboard-table leaderboard-table--monthly">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Pseudo</th>
                                                    <th>Flashs</th>
                                                    <th>Points</th>
                                                    <th>Nouvelles villes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {monthlyStats.map((entry, index) => (
                                                    <tr key={entry.odri} className="leaderboard-table__row">
                                                        <td className={`leaderboard-table__cell--rank ${getMedalClass(index + 1)}`}>
                                                            {index + 1}
                                                        </td>
                                                        <td className="leaderboard-table__cell--pseudo">
                                                            {entry.pseudo || "-"}
                                                        </td>
                                                        <td className="leaderboard-table__cell--flashs">
                                                            {entry.flashCount}
                                                        </td>
                                                        <td className="leaderboard-table__cell--points">
                                                            +{entry.pointsGained.toLocaleString()}
                                                        </td>
                                                        <td className="leaderboard-table__cell--new-cities">
                                                            {entry.newCities.length > 0 ? entry.newCities.join(', ') : '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default LeaderboardPage;
